
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import { debounce } from "typescript-debounce-decorator";
import moment from "moment";
import httpClient from "../../store/httpClient";
import DatePicker from "@/components/DatePicker.vue";
import { PaymentDto } from "@/dtos/paymentDto";
import { PaymentSearchRequestDto } from "@/dtos/paymentSearchRequestDto";

@Component({
	components: {
		DatePicker,
	},
})
export default class Payments extends Vue {
	@Prop()
	private isLoading?: boolean;
	@Prop()
	private invoiceIdFilter?: number;
	private startDate: string = moment().subtract(2, "months").format("YYYY-MM-01");
	private endDate: string = moment(moment().format("YYYY-MM-01"))
		.add(1, "months")
		.subtract(1, "days")
		.format("YYYY-MM-DD");
	private headers: Array<{
		text: string;
		value: string;
		align: string;
		sortable: boolean;
		width: string;
	}> = Array(
		{
			text: "Id",
			value: "id",
			align: "left",
			sortable: true,
			width: "1px",
		},
		{
			text: "Rechnungs-Id",
			value: "invoiceId",
			align: "left",
			sortable: true,
			width: "1px",
		},
		{
			text: "Kunden-Id",
			value: "customerId",
			align: "left",
			sortable: true,
			width: "1px",
		},
		{
			text: "Name",
			value: "customerInvoiceName",
			align: "left",
			sortable: true,
			width: "1px",
		},
		{
			text: "Auftrags-Id",
			value: "customerOrderId",
			align: "left",
			sortable: true,
			width: "1px",
		},
		{
			text: "Zahlungsdatum",
			value: "paymentDate",
			align: "left",
			sortable: true,
			width: "1px",
		},
		{
			text: "Region",
			value: "region",
			align: "left",
			sortable: true,
			width: "1px",
		},
		{
			text: "Infos",
			value: "information",
			align: "left",
			sortable: false,
			width: "1px",
		},
		{
			text: "Betrag",
			value: "amount",
			align: "right",
			sortable: true,
			width: "1px",
		}
	);

	private mounted() {
		if (this.payments.length === 0) {
			this.loadPayments();
		}
	}

	@Watch("invoiceIdFilter")
	private setInvoiceIdFilter() {
		if (this.invoiceIdFilter && this.invoiceIdFilter > 0) {
			window.scrollTo(0, 0);
			this.searchTerm = this.invoiceIdFilter.toString();
		}
	}

	private get filteredPayments() {
		let storePayments = this.payments;
		storePayments = storePayments.filter((p: PaymentDto) =>
			this.filterRegions.includes(p.region)
		);
		return storePayments;
	}

	private get payments() {
		return this.$store.state.payments;
	}
	private set payments(newPayments: PaymentDto[]) {
		this.$store.commit("setPayments", newPayments);
	}

	private get searchTerm(): string {
		return this.$store.state.paymentSearchTerm;
	}
	private set searchTerm(searchTerm: string) {
		this.$store.commit("setPaymentSearchTerm", searchTerm);
	}

	private get lastSearchTerm(): string {
		return this.$store.state.paymentLastSearchTerm;
	}
	private set lastSearchTerm(lastSearchTerm: string) {
		this.$store.commit("setPaymentLastSearchTerm", lastSearchTerm);
	}

	private get regions() {
		return this.$store.state.regions;
	}
	private get filterRegions() {
		return this.$store.state.paymentFilterRegions;
	}
	private set filterRegions(regions: string[]) {
		this.$store.commit("setPaymentFilterRegions", regions);
	}

	private get paymentSorting() {
		return this.$store.state.paymentSorting;
	}
	private set paymentSorting(paymentSorting: any) {
		this.$store.commit("setPaymentSorting", paymentSorting);
	}

	private get totalPaymentBalanceChf() {
		return this.payments
			.filter((i: PaymentDto) => i.currency === "CHF")
			.reduce((acc: number, i: PaymentDto) => {
				return acc + i.amount;
			}, 0);
	}
	private get totalPaymentBalanceEur() {
		return this.payments
			.filter((i: PaymentDto) => i.currency === "EUR")
			.reduce((acc: number, i: PaymentDto) => {
				return acc + i.amount;
			}, 0);
	}

	private get filteredPaymentBalanceChf() {
		return this.filteredPayments
			.filter((i: PaymentDto) => i.currency === "CHF")
			.reduce((acc: number, i: PaymentDto) => {
				return acc + i.amount;
			}, 0);
	}
	private get filteredPaymentBalanceEur() {
		return this.filteredPayments
			.filter((i: PaymentDto) => i.currency === "EUR")
			.reduce((acc: number, i: PaymentDto) => {
				return acc + i.amount;
			}, 0);
	}

	private changeSort(column: string) {
		if (this.paymentSorting.sortBy === column) {
			this.paymentSorting.descending = !this.paymentSorting.descending;
		} else {
			this.paymentSorting.sortBy = column;
			this.paymentSorting.descending = false;
		}
	}

	private async navigateToInvoiceEdit(invoiceId: number) {
		this.$router.push({ name: "editInvoice", params: { invoiceId: invoiceId.toString() } });
	}

	private async loadPayments() {
		if (this.isLoading) {
			return;
		}
		try {
			this.$emit("setIsLoading", true);
			console.log("Loading payments...");
			let searchRequest: PaymentSearchRequestDto = {
				paymentIds: [],
				searchTerm: this.searchTerm,
				sortingType: 1,
				dateFrom: new Date(this.startDate),
				dateTo: new Date(this.endDate),
			};
			const response = await httpClient().post("Invoice/GetPayments?", searchRequest);
			this.payments = response.data;
		} catch (e) {
			console.log("Loading payments failed.", e);
		} finally {
			this.$emit("setIsLoading", false);
		}
	}

	private goToCustomer(customerId: string) {
		this.$router.push({ name: "customerDetail", params: { customerId } });
	}

	@debounce(700, { leading: false })
	@Watch("searchTerm")
	private reloadOnSearchTermChange() {
		if (
			!this.isLoading &&
			this.lastSearchTerm !== this.searchTerm &&
			(this.searchTerm.length >= 3 || this.searchTerm.length === 0)
		) {
			this.lastSearchTerm = this.searchTerm;
			this.loadPayments();
		}
	}

	private setStartDate(date: Date) {
		this.startDate = moment(date).format("YYYY-MM-DD");
	}
	private setEndDate(date: Date) {
		this.endDate = moment(date).format("YYYY-MM-DD");
	}
	private setThisMonth() {
		this.startDate = moment().format("YYYY-MM-01");
		this.endDate = moment(moment().format("YYYY-MM-01"))
			.add(1, "months")
			.subtract(1, "days")
			.format("YYYY-MM-DD");
	}
	private shiftMonth(direction: string) {
		if (direction === "forward") {
			this.startDate = moment(this.startDate).add(1, "months").format("YYYY-MM-01");
			this.endDate = moment(moment(this.endDate).add(1, "months").format("YYYY-MM-01"))
				.add(1, "months")
				.subtract(1, "days")
				.format("YYYY-MM-DD");
		} else {
			this.startDate = moment(this.startDate).subtract(1, "months").format("YYYY-MM-01");
			this.endDate = moment(moment(this.endDate).subtract(1, "months").format("YYYY-MM-01"))
				.add(1, "months")
				.subtract(1, "days")
				.format("YYYY-MM-DD");
		}
	}
}
